import { useEffect, useState } from "react";
import moment from "moment";
import Table from "../../components/advancedTable";
import Select from "react-select";
import { publicRequest } from "../../requestMethods";
import { useHistory } from "react-router";
import { Edit, LocalGroceryStore, Close } from "@material-ui/icons";
import "../orderList/orderList.css";

const findOption = (options, value) =>
    options.find((opt) => opt.value === value) || null;

const statusOptions = [
    { label: "All", value: "" },
    { label: "Active", value: "active" },
    { label: "Inactive", value: "inactive" },
];

const typeOptions = [
    { label: "All", value: "" },
    { label: "Monthly", value: "monthly" },
    { label: "Quarterly", value: "quarterly" },
    { label: "Half-Yearly", value: "half-yearly" },
    { label: "Yearly", value: "yearly" },
];

export default function TermsList() {
    const history = useHistory();
    const [termsData, setTermsData] = useState([]);
    const [usersData, setUsersData] = useState([]);
    const [isLoaded, setIsLoaded] = useState(false);
    const [statusFilter, setStatusFilter] = useState("");
    const [typeFilter, setTypeFilter] = useState("");
    const [userFilter, setUserFilter] = useState("");
    const [filterResetKey, setFilterResetKey] = useState(0);
    const [productsPopup, setProductsPopup] = useState(false);
    const [productsModalData, setProductsModalData] = useState([]);

    const formatter = new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

    const productColumns = [
        {
            accessor: "code",
            Header: "Name",
            sortable: false,
            Cell: ({ value }) => (
                <div className="initialPadding">{value || "—"}</div>
            ),
        },
        {
            accessor: "quantity",
            Header: "Quantity",
            sortable: false,
            Cell: ({ value }) => (
                <div className="initialPadding">{value ?? "—"}</div>
            ),
        },
        {
            accessor: "amount",
            Header: "Total Price",
            sortable: false,
            Cell: (params) => (
                <div className="initialPadding">
                    RM{" "}
                    {params.row.original.amount != null
                        ? formatter.format(params.row.original.amount)
                        : params.row.original.price != null &&
                          params.row.original.quantity != null
                        ? formatter.format(
                              params.row.original.price *
                                  params.row.original.quantity
                          )
                        : "0"}
                </div>
            ),
        },
    ];

    useEffect(() => {
        const fetchTerms = async () => {
            try {
                const params = {};
                if (statusFilter) params.status = statusFilter;
                if (typeFilter) params.type = typeFilter;
                if (userFilter) params.userId = userFilter;
                const res = await publicRequest.get("/terms", { params });
                setTermsData(res?.data || []);
            } catch (err) {
                console.error(err);
            }
            setIsLoaded(true);
        };
        fetchTerms();
    }, [statusFilter, typeFilter, userFilter]);

    const handleResetFilters = () => {
        setStatusFilter("");
        setTypeFilter("");
        setUserFilter("");
        setFilterResetKey((prev) => prev + 1);
    };

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const res = await publicRequest.get("/users/all");
                setUsersData(res?.data || []);
            } catch (err) {
                console.error(err);
            }
        };
        fetchUsers();
    }, []);

    const statusSelectOptions = statusOptions.map((s) => ({
        label: s.label,
        value: s.value,
    }));
    const typeSelectOptions = typeOptions.map((t) => ({
        label: t.label,
        value: t.value,
    }));
    const userOptions = [
        { label: "All", value: "" },
        ...usersData.map((u) => ({
            label: u.name || u.email || u._id,
            value: u._id,
        })),
    ];

    const columns = [
        {
            accessor: "userId",
            Header: "User",
            sortable: false,
            Cell: ({ value }) => (
                <div className="initialPadding">
                    {value?.name ?? value?.email ?? "—"}
                </div>
            ),
        },
        {
            accessor: "date",
            Header: "Date",
            sortable: false,
            Cell: ({ value }) => (
                <div className="capitalize">
                    {value ? moment(value).format("DD/MM/YYYY") : "—"}
                </div>
            ),
        },
        {
            accessor: "status",
            Header: "Status",
            sortable: false,
            Cell: ({ value }) => (
                <div className="capitalize">{value ?? "—"}</div>
            ),
        },
        {
            accessor: "type",
            Header: "Type",
            sortable: false,
            Cell: ({ value }) => (
                <div className="capitalize">
                    {value ? value.replace("-", " ") : "—"}
                </div>
            ),
        },
        {
            accessor: "products",
            Header: "Products",
            sortable: false,
            Cell: (params) => {
                const productsList =
                    params.row.original.products || [];
                return (
                    <LocalGroceryStore
                        className="icon pointer text-black hover:text-gray-700 transition-colors duration-200"
                        titleAccess="View products"
                        onClick={() => {
                            setProductsModalData(productsList);
                            setProductsPopup(true);
                        }}
                    />
                );
            },
        },
        {
            accessor: "totalAmount",
            Header: "Total Amount",
            sortable: false,
            Cell: (params) => {
                const products = params.row.original.products || [];
                const total = products.reduce((sum, p) => {
                    const lineTotal =
                        p.amount != null
                            ? Number(p.amount)
                            : (Number(p.price) || 0) * (Number(p.quantity) || 1);
                    return sum + lineTotal;
                }, 0);
                return (
                    <div className="initialPadding">
                        RM {formatter.format(total)}
                    </div>
                );
            },
        },
        {
            accessor: "action",
            Header: "Actions",
            sortable: false,
            Cell: (params) => (
                <button
                    onClick={() =>
                        history.push(`/terms/${params.row.original._id}`)
                    }
                    className="px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 flex items-center gap-1"
                >
                    <Edit fontSize="small" />
                    Edit
                </button>
            ),
        },
    ];

    return (
        <div className="w-full bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
            <div className="productList bg-white rounded-3xl shadow-2xl p-8 m-6 border border-slate-200/50">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                    <div className="space-y-2">
                        <label className="text-sm font-semibold text-slate-700 tracking-wide">
                            User
                        </label>
                        <div className="luxury-select-wrapper">
                            <Select
                                key={`user-${filterResetKey}`}
                                options={userOptions}
                                value={findOption(userOptions, userFilter)}
                                onChange={(opt) =>
                                    setUserFilter(opt?.value ?? "")
                                }
                                placeholder="Select User"
                                styles={{
                                    control: (base) => ({
                                        ...base,
                                        background:
                                            "linear-gradient(to right, rgb(248 250 252), rgb(239 246 255))",
                                        border: "2px solid rgb(226 232 240)",
                                        borderRadius: "12px",
                                        padding: "6px",
                                        fontSize: "14px",
                                        fontWeight: "500",
                                        boxShadow: "none",
                                        "&:hover": {
                                            borderColor: "rgb(59 130 246)",
                                        },
                                    }),
                                    option: (base, state) => ({
                                        ...base,
                                        backgroundColor: state.isSelected
                                            ? "rgb(59 130 246)"
                                            : state.isFocused
                                            ? "rgb(239 246 255)"
                                            : "white",
                                        color: state.isSelected
                                            ? "white"
                                            : "rgb(51 65 85)",
                                        fontWeight: "500",
                                        "&:hover": {
                                            backgroundColor: "rgb(239 246 255)",
                                        },
                                    }),
                                    menu: (base) => ({
                                        ...base,
                                        zIndex: 9999,
                                    }),
                                    menuPortal: (base) => ({
                                        ...base,
                                        zIndex: 9999,
                                    }),
                                }}
                                menuPortalTarget={document.body}
                            />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <label className="text-sm font-semibold text-slate-700 tracking-wide">
                            Status
                        </label>
                        <div className="luxury-select-wrapper">
                            <Select
                                key={`status-${filterResetKey}`}
                                options={statusSelectOptions}
                                value={findOption(
                                    statusSelectOptions,
                                    statusFilter
                                )}
                                onChange={(opt) =>
                                    setStatusFilter(opt?.value ?? "")
                                }
                                placeholder="Select Status"
                                styles={{
                                    control: (base) => ({
                                        ...base,
                                        background:
                                            "linear-gradient(to right, rgb(248 250 252), rgb(239 246 255))",
                                        border: "2px solid rgb(226 232 240)",
                                        borderRadius: "12px",
                                        padding: "6px",
                                        fontSize: "14px",
                                        fontWeight: "500",
                                        boxShadow: "none",
                                        "&:hover": {
                                            borderColor: "rgb(59 130 246)",
                                        },
                                    }),
                                    option: (base, state) => ({
                                        ...base,
                                        backgroundColor: state.isSelected
                                            ? "rgb(59 130 246)"
                                            : state.isFocused
                                            ? "rgb(239 246 255)"
                                            : "white",
                                        color: state.isSelected
                                            ? "white"
                                            : "rgb(51 65 85)",
                                        fontWeight: "500",
                                        "&:hover": {
                                            backgroundColor: "rgb(239 246 255)",
                                        },
                                    }),
                                    menu: (base) => ({
                                        ...base,
                                        zIndex: 9999,
                                    }),
                                    menuPortal: (base) => ({
                                        ...base,
                                        zIndex: 9999,
                                    }),
                                }}
                                menuPortalTarget={document.body}
                            />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <label className="text-sm font-semibold text-slate-700 tracking-wide">
                            Type
                        </label>
                        <div className="luxury-select-wrapper">
                            <Select
                                key={`type-${filterResetKey}`}
                                options={typeSelectOptions}
                                value={findOption(typeSelectOptions, typeFilter)}
                                onChange={(opt) =>
                                    setTypeFilter(opt?.value ?? "")
                                }
                                placeholder="Select Type"
                                styles={{
                                    control: (base) => ({
                                        ...base,
                                        background:
                                            "linear-gradient(to right, rgb(248 250 252), rgb(239 246 255))",
                                        border: "2px solid rgb(226 232 240)",
                                        borderRadius: "12px",
                                        padding: "6px",
                                        fontSize: "14px",
                                        fontWeight: "500",
                                        boxShadow: "none",
                                        "&:hover": {
                                            borderColor: "rgb(59 130 246)",
                                        },
                                    }),
                                    option: (base, state) => ({
                                        ...base,
                                        backgroundColor: state.isSelected
                                            ? "rgb(59 130 246)"
                                            : state.isFocused
                                            ? "rgb(239 246 255)"
                                            : "white",
                                        color: state.isSelected
                                            ? "white"
                                            : "rgb(51 65 85)",
                                        fontWeight: "500",
                                        "&:hover": {
                                            backgroundColor: "rgb(239 246 255)",
                                        },
                                    }),
                                    menu: (base) => ({
                                        ...base,
                                        zIndex: 9999,
                                    }),
                                    menuPortal: (base) => ({
                                        ...base,
                                        zIndex: 9999,
                                    }),
                                }}
                                menuPortalTarget={document.body}
                            />
                        </div>
                    </div>
                </div>

                <div className="flex justify-start gap-4 mb-8 flex-wrap">
                    <button
                        onClick={() => history.push("/newterms")}
                        className="px-8 py-4 bg-blue-500 hover:bg-blue-600 text-white font-bold rounded-xl shadow-xl transition-all duration-300 transform hover:scale-105 hover:shadow-2xl flex items-center gap-3"
                    >
                        <span className="text-lg">+</span>
                        <span className="tracking-wide">Add Terms</span>
                    </button>
                    <button
                        onClick={handleResetFilters}
                        className="px-8 py-4 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-bold rounded-xl shadow-xl transition-all duration-300 transform hover:scale-105 hover:shadow-2xl flex items-center gap-3 border border-red-400"
                    >
                        <span className="text-white text-lg">🔄</span>
                        <span className="tracking-wide">Reset Filters</span>
                    </button>
                </div>
                {isLoaded && (
                    <Table
                        data={termsData}
                        columns={columns}
                        pageCount={Math.ceil(termsData.length / 20)}
                        totalCount={termsData.length}
                        updatePagination={() => {}}
                        paginationState={{ page: 1, limit: 20 }}
                        updateLoaded={() => {}}
                        hidePagination={termsData.length <= 20}
                    />
                )}
            </div>
            {productsPopup && (
                <div className="overlayContainer">
                    <div className="bigcenter">
                        <div
                            className="close"
                            onClick={() => setProductsPopup(false)}
                        >
                            <Close />
                        </div>
                        <div className="updatemodalContainer">
                            <div className="updateOrder">
                                <h1 className="updateProductTitle text-xl font-semibold">
                                    Products ({productsModalData.length})
                                </h1>
                                <Table
                                    data={productsModalData}
                                    columns={productColumns}
                                    hidePagination={true}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
